import * as React from 'react';
import { renderToString } from 'react-dom/server';

import AssignmentCard from './assignment_card';

import ScrollToTop from '../../shared/scroll_to_top';
import * as constants from '../assignmentFlowConstants';
import AssignmentFlowNavigation from '../assignment_flow_navigation';
import { generateRecommendations, STARTER, INTERMEDIATE, ADVANCED, DIAGNOSTIC_LEVELS, } from '../../progress_reports/diagnostic_insights/shared'
import { getPageTitle, pluralize, SMALL_ICON_BASE_SRC, Tooltip, } from '../../../../Shared';
import { requestGet, } from '../../../../../modules/request'

const ALL = 'All'
const GENERAL = 'General'
const ELL = 'ELL'
const COLLEGEBOARD = 'CollegeBoard'
const ELL_STARTER = `ell_${STARTER}`
const ELL_INTERMEDIATE = `ell_${INTERMEDIATE}`
const ELL_ADVANCED = `ell_${ADVANCED}`

interface RecommendationsByClassroom {
  id: number;
  name: string;
  studentNames: string[];
}

interface RecommendedDiagnosticsByLevelInterface {
  [STARTER]: RecommendationsByClassroom[];
  [INTERMEDIATE]: RecommendationsByClassroom[];
  [ADVANCED]: RecommendationsByClassroom[];
  [ELL_STARTER]: RecommendationsByClassroom[];
  [ELL_INTERMEDIATE]: RecommendationsByClassroom[];
  [ELL_ADVANCED]: RecommendationsByClassroom[];
}

const selectCard = (history: any, unitTemplateName: string, activityIdsArray: string, unitTemplateId: number) => {
  const unitTemplateIdString = unitTemplateId.toString();
  window.localStorage.setItem(constants.UNIT_TEMPLATE_NAME, unitTemplateName)
  window.localStorage.setItem(constants.UNIT_NAME, unitTemplateName)
  window.localStorage.setItem(constants.ACTIVITY_IDS_ARRAY, activityIdsArray)
  window.localStorage.setItem(constants.UNIT_TEMPLATE_ID, unitTemplateIdString)
  history.push(`/assign/select-classes?diagnostic_unit_template_id=${unitTemplateIdString}`)
}

interface Diagnostic {
  activityId: number;
  name: string;
  unitTemplateId: number;
  what: string;
  when: string;
  lockedText?: string
}

const DiagnosticAssignmentCard = ({ history, diagnostic, lockedText, recommendedClassrooms, }: { history: any, diagnostic: Diagnostic, lockedText?: string, recommendedClassrooms?: RecommendationsByClassroom[] }) => {
  function onSelectCard() { selectCard(history, diagnostic.name, encodeURIComponent([diagnostic.activityId].toString()), diagnostic.unitTemplateId) }

  let callout

  if (recommendedClassrooms?.length) {
    const totalRecommendedStudents = recommendedClassrooms?.reduce((sum, recommendedDiagnostic) => sum += recommendedDiagnostic.studentNames.length, 0)
    const tooltipText = (
      <React.Fragment>
        <p>This diagnostic is recommended for <b>{totalRecommendedStudents} {pluralize(totalRecommendedStudents, 'student', 'students')}</b> in <b>{recommendedClassrooms.length} {pluralize(recommendedClassrooms.length, 'class', 'classes')}</b> based on their history:</p>
        {recommendedClassrooms.map(recommendedDiagnostic => {
          return (
            <p key={recommendedDiagnostic.id}><b>{recommendedDiagnostic.name} ({recommendedDiagnostic.studentNames.length}):</b> {recommendedDiagnostic.studentNames.join(', ')}</p>
          )
        })}
      </React.Fragment>
    )

    callout = (
      <div className="callout">
        <img alt="" src={`${SMALL_ICON_BASE_SRC}/lightbulb.svg`} />
        <p>Recommended for&#8197;
          <Tooltip
            tooltipText={renderToString(tooltipText)}
            tooltipTriggerText={`${totalRecommendedStudents} ${pluralize(totalRecommendedStudents, 'student', 'students')} in ${recommendedClassrooms.length} ${pluralize(recommendedClassrooms.length, 'class', 'classes')}`}
          />
          &#8197;based on their history.&#8197;
          <a className="focus-on-light" href="/teachers/progress_reports/diagnostic_insights" rel='noopener noreferrer' target="_blank">Learn&nbsp;more</a>
        </p>
      </div>
    )
  }

  return (
    <AssignmentCard
      bodyArray={[
        { key: 'What', text: diagnostic.what, },
        { key: 'When', text: diagnostic.when, }
      ]}
      buttonLink={`/activity_sessions/anonymous?activity_id=${diagnostic.activityId}`}
      buttonText="Preview"
      callout={callout}
      header={diagnostic.name}
      key={diagnostic.activityId}
      lockedText={lockedText}
      selectCard={onSelectCard}
    />
  )
}

const generalDiagnosticMinis = ({ history, assignedPreTests, recommendedDiagnosticsByLevel, }) => {
  function isLocked(postTestActivityId) {
    const assignedPreTest = assignedPreTests.find(pretest => pretest.post_test_id === postTestActivityId)
    return assignedPreTest && !assignedPreTest.assigned_classroom_ids.length
  }

  return [
    (<div className="grouped-minis" key="starter">
      <DiagnosticAssignmentCard diagnostic={constants.starterPreTest} history={history} recommendedClassrooms={recommendedDiagnosticsByLevel[STARTER]} />
      <DiagnosticAssignmentCard diagnostic={constants.starterPostTest} history={history} lockedText={isLocked(constants.starterPostTest.activityId) && constants.starterPostTest.lockedText} />
    </div>),
    (<div className="grouped-minis" key="intermediate">
      <DiagnosticAssignmentCard diagnostic={constants.intermediatePreTest} history={history} recommendedClassrooms={recommendedDiagnosticsByLevel[INTERMEDIATE]} />
      <DiagnosticAssignmentCard diagnostic={constants.intermediatePostTest} history={history} lockedText={isLocked(constants.intermediatePostTest.activityId) && constants.intermediatePostTest.lockedText} />
    </div>),
    (<div className="grouped-minis" key="advanced">
      <DiagnosticAssignmentCard diagnostic={constants.advancedPreTest} history={history} recommendedClassrooms={recommendedDiagnosticsByLevel[ADVANCED]} />
      <DiagnosticAssignmentCard diagnostic={constants.advancedPostTest} history={history} lockedText={isLocked(constants.advancedPostTest.activityId) && constants.advancedPostTest.lockedText} />
    </div>)
  ]
}

const ellDiagnosticMinis = ({ history, assignedPreTests, recommendedDiagnosticsByLevel, }) => {
  function isLocked(postTestActivityId) {
    const assignedPreTest = assignedPreTests.find(pretest => pretest.post_test_id === postTestActivityId)
    return assignedPreTest && !assignedPreTest.assigned_classroom_ids.length
  }

  return [
    (<div className="grouped-minis" key="ell-starter">
      <DiagnosticAssignmentCard diagnostic={constants.ellStarterPreTest} history={history} recommendedClassrooms={recommendedDiagnosticsByLevel[ELL_STARTER]} />
      <DiagnosticAssignmentCard diagnostic={constants.ellStarterPostTest} history={history} lockedText={isLocked(constants.ellStarterPostTest.activityId) && constants.ellStarterPostTest.lockedText} />
    </div>),
    (<div className="grouped-minis" key="ell-intermediate">
      <DiagnosticAssignmentCard diagnostic={constants.ellIntermediatePreTest} history={history} recommendedClassrooms={recommendedDiagnosticsByLevel[ELL_INTERMEDIATE]} />
      <DiagnosticAssignmentCard diagnostic={constants.ellIntermediatePostTest} history={history} lockedText={isLocked(constants.ellIntermediatePostTest.activityId) && constants.ellIntermediatePostTest.lockedText} />
    </div>),
    (<div className="grouped-minis" key="ell-advanced">
      <DiagnosticAssignmentCard diagnostic={constants.ellAdvancedPreTest} history={history} recommendedClassrooms={recommendedDiagnosticsByLevel[ELL_ADVANCED]} />
      <DiagnosticAssignmentCard diagnostic={constants.ellAdvancedPostTest} history={history} lockedText={isLocked(constants.ellAdvancedPostTest.activityId) && constants.ellAdvancedPostTest.lockedText} />
    </div>)
  ]
}

const collegeBoardDiagnosticMinis = ({ history }) => [
  <DiagnosticAssignmentCard diagnostic={constants.apWritingSkills} history={history} key="ap" />,
  <DiagnosticAssignmentCard diagnostic={constants.preAPWritingSkills1} history={history} key="pre-ap-1" />,
  <DiagnosticAssignmentCard diagnostic={constants.preAPWritingSkills2} history={history} key="pre-ap-2" />,
  <DiagnosticAssignmentCard diagnostic={constants.springboardWritingSkills} history={history} key="springboard" />
];

const FilterTab = ({ activeFilter, filter, setFilter, number, }) => {
  function handleClick() { setFilter(filter) }
  const className = activeFilter === filter ? 'active filter-tab focus-on-light' : 'filter-tab focus-on-light'
  return <button className={className} onClick={handleClick} role="tab" type="button">{filter} ({number})</button>
}

const AssignADiagnostic = ({ history, assignedPreTests, }) => {
  document.title = getPageTitle("Assign", "Diagnostic")
  const [filter, setFilter] = React.useState(ALL)
  const [classCount, setClassCount] = React.useState<number>(null)
  const [recommendationsCount, setRecommendationsCount] = React.useState<number>(null)
  const [recommendedDiagnosticsByLevel, setRecommendedDiagnosticsByLevel] = React.useState<RecommendedDiagnosticsByLevelInterface>({})

  React.useEffect(() => {
    getCompleteHistory()
  }, [])

  function getCompleteHistory() {
    requestGet('/diagnostic_insights/complete_history', (body) => {
      let classCount = 0
      let recommendationsCount = 0
      const newRecommendedDiagnosticsByLevel: RecommendedDiagnosticsByLevelInterface = {
        [STARTER]: [],
        [INTERMEDIATE]: [],
        [ADVANCED]: [],
        [ELL_STARTER]: [],
        [ELL_INTERMEDIATE]: [],
        [ELL_ADVANCED]: []
      }

      Object.keys(body).forEach((classroomId) => {
        const classroom = body[classroomId]
        const ellLearningSequences = classroom.ell.filter(student => Object.keys(student.student_learning_sequences).length)
        const nonELLLearningSequences = classroom.non_ell.filter(student => Object.keys(student.student_learning_sequences).length)

        const ellRecommendations = generateRecommendations(true, ellLearningSequences, classroomId)
        const nonELLRecommendations = generateRecommendations(false, nonELLLearningSequences, classroomId)

        let ellRecommendationsCount = 0
        let nonELLRecommendationsCount = 0

        DIAGNOSTIC_LEVELS.forEach((diagnosticLevel) => {
          if (ellRecommendations[diagnosticLevel].student_ids.length) {
            ellRecommendationsCount += ellRecommendations[diagnosticLevel].student_ids.length
            const recommendedStudentNames = ellLearningSequences
              .filter((studentLearningSequence) => ellRecommendations[diagnosticLevel].student_ids.includes(studentLearningSequence.user_id))
              .map(studentLearningSequence => studentLearningSequence.user_name)
            newRecommendedDiagnosticsByLevel[`ell_${diagnosticLevel}`].push({ id: classroomId, name: classroom.classroom_name, studentNames: recommendedStudentNames})
          }

          if (nonELLRecommendations[diagnosticLevel].student_ids.length) {
            nonELLRecommendationsCount += nonELLRecommendations[diagnosticLevel].student_ids.length
            const recommendedStudentNames = nonELLLearningSequences
              .filter((studentLearningSequence) => nonELLRecommendations[diagnosticLevel].student_ids.includes(studentLearningSequence.user_id))
              .map(studentLearningSequence => studentLearningSequence.user_name)
            newRecommendedDiagnosticsByLevel[diagnosticLevel].push({ id: classroomId, name: classroom.classroom_name, studentNames: recommendedStudentNames})
          }
        })

        if (ellRecommendationsCount || nonELLRecommendationsCount) {
          classCount += 1
          recommendationsCount += ellRecommendationsCount + nonELLRecommendationsCount
        }
      })

      setClassCount(classCount)
      setRecommendationsCount(recommendationsCount)
      setRecommendedDiagnosticsByLevel(newRecommendedDiagnosticsByLevel)
    })
  }

  const general = generalDiagnosticMinis({ history, assignedPreTests, recommendedDiagnosticsByLevel, })
  const ell = ellDiagnosticMinis({ history, assignedPreTests, recommendedDiagnosticsByLevel, })
  const collegeBoard = collegeBoardDiagnosticMinis({ history, })
  const all = [general, ell, collegeBoard].flat()
  let minis = all
  switch(filter) {
    case GENERAL:
      minis = general;
      break;
    case ELL:
      minis = ell;
      break;
    case COLLEGEBOARD:
      minis = collegeBoard;
      break;
  }
  return (
    <div className="assignment-flow-container">
      <AssignmentFlowNavigation />
      <ScrollToTop />
      <main className="diagnostic-page container">
        <h1>Which diagnostic covers the skills you want to assess?</h1>
        {classCount > 0 && <section className='recommended-diagnostics-banner'>
          <p>Not sure where to start? See recommendations for {recommendationsCount} {pluralize(recommendationsCount, 'student', 'students')} in {classCount} {pluralize(classCount, 'class', 'classes')}.</p>
          <a className="focus-on-light" href="/teachers/progress_reports/diagnostic_insights" rel='noopener noreferrer' target="_blank">Learn more</a>
        </section>}
        <div className="filter-tabs" role="tablist">
          <FilterTab activeFilter={filter} filter={ALL} number={16} setFilter={setFilter} />
          <FilterTab activeFilter={filter} filter={GENERAL} number={6} setFilter={setFilter} />
          <FilterTab activeFilter={filter} filter={ELL} number={6} setFilter={setFilter} />
          <FilterTab activeFilter={filter} filter={COLLEGEBOARD} number={4} setFilter={setFilter} />
        </div>
        <section aria-label="Diagnostic Options" className="minis">{minis}</section>
      </main>
    </div>
  )
};

export default AssignADiagnostic
